import {ChangeDetectorRef, Component, OnInit,
  CUSTOM_ELEMENTS_SCHEMA} from '@angular/core';
import {Observable, of} from 'rxjs';
import {catchError, switchMap, take} from 'rxjs/operators';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {AuthStorageService, GlobalMessageService, GlobalMessageType, User} from '@spartacus/core';
import {UserAccountFacade} from '@spartacus/user/account/root';
import {ConfigService, ProductDetermination, LoadingPageComponent} from '@dcp/configurator';
import {DcpCtaValidationService} from '@app/services/dcp-cta-validation';
import {TransactionTypeService} from '@app/services/transactionTypes/transaction-type.service';
import {GraphqlEdgeService} from '@app/services/dcp-graphql-edge';
import {convertUrlToCurrentHostname} from './../shared/url-utils';
import {getAuthorized} from './../shared/getAuthorized';
import {formatTestPageError} from './../shared/format-error';
import {TabNavigationComponent} from '../tab-navigation/tab-navigation.component';
import {IframeComponent} from '../../shared/iframe/iframe.component';
import {CdkCopyToClipboard} from '@angular/cdk/clipboard';
import {AsyncPipe} from '@angular/common';

@UntilDestroy()
@Component({
  selector: 'dcp-cta-validation-page',
  templateUrl: './cta-validation-page.component.html',
  styleUrls: ['./cta-validation-page.component.scss'],
  imports: [LoadingPageComponent, TabNavigationComponent, IframeComponent, CdkCopyToClipboard, AsyncPipe],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class CtaValidationPageComponent implements OnInit {
  technicalName = '';
  materialNumber = '';
  sUserId = '';
  currentUser: string;
  callToAction: string;
  isAuthorized: boolean;
  user$: Observable<User>;
  ctaValidationResult$: Observable<ProductDetermination>;
  previewUrl: string;
  loadingState = false;
  loadingScreenText = 'Please wait while the cached list is updated';
  currentUserScimUserId: string;
  ctaOptions: {label: string; value: string}[] = [];

  constructor(
    private dcpCtaValidationService: DcpCtaValidationService,
    private userFacade: UserAccountFacade,
    private globalMessageService: GlobalMessageService,
    private authStorageService: AuthStorageService,
    private configService: ConfigService,
    private cdr: ChangeDetectorRef,
    private transactionTypeService: TransactionTypeService,
    private graphqlEdgeService: GraphqlEdgeService,
  ) {}

  /**
   * Angular lifecycle hook. Initializes user stream, authorization, and CTA options.
   */
  ngOnInit(): void {
    this.user$ = this.userFacade.get();
    this.user$.pipe(untilDestroyed(this), take(1)).subscribe((user) => {
      this.currentUserScimUserId = user.scimUserId;
    });
    this.getAuthorizedRoles();
    this.transactionTypeService
      .getTransactionTypes$()
      .pipe(untilDestroyed(this))
      .subscribe((types) => {
        this.ctaOptions = (types || []).map((type) => ({label: type.code, value: type.code}));
      });
  }

  convertUrlToCurrentHostname = convertUrlToCurrentHostname;

  /**
   * Handles input events from text fields and updates the corresponding properties.
   * @param event CustomEvent containing input data.
   */
  onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    switch (target.id) {
      case 'technicalName':
        this.technicalName = target.value;
        break;
      case 'materialNumber':
        this.materialNumber = target.value;
        break;
      case 'sUserId':
        this.sUserId = target.value;
        break;
      default:
        break;
    }
  }

  /**
   * Handles CTA selection from the UI.
   * @param event CustomEvent containing selection data.
   */
  onInputCta(event: CustomEvent): void {
    this.callToAction = event.detail.item.value;
  }

  /**
   * Resets the CTA validation form and result.
   */
  onReset(): void {
    this.technicalName = '';
    this.callToAction = '';
    this.materialNumber = '';
    this.sUserId = '';
    this.ctaValidationResult$ = of(undefined);
  }

  /**
   * Fetch the slmId using technicalName and materialNumber.
   * @param technicalName The technical name of the product.
   * @param materialNumber The material number of the edition.
   * @returns Observable emitting the slmId as a string.
   */
  private fetchSlmId(technicalName: string, materialNumber: string): Observable<string> {
    return this.graphqlEdgeService.getProductByTechnicalName(technicalName).pipe(
      switchMap((response: any) => {
        const products = response?.data?.showCosmosDbProductsV3?.products || [];
        if (!products.length) return of('');
        const editions = products[0]?.commercial?.[0]?.editions || [];
        const edition = editions.find((e) => e.materialNumber === materialNumber);
        return of(edition ? edition.id : '');
      }),
    );
  }

  /**
   * Fetches CTA validation result and assigns it to the observable.
   * Handles errors by displaying a global message.
   * @param technicalName The technical name of the product.
   * @param materialNumber The material number.
   * @param sUserId The S-User ID.
   * @param callToAction The CTA code.
   */
  getCtaValidation(technicalName?: string, materialNumber?: string, sUserId?: string, callToAction?: string): void {
    this.ctaValidationResult$ = this.fetchSlmId(technicalName, materialNumber).pipe(
      switchMap((slmId: string) =>
        this.dcpCtaValidationService.getCtaValidation(sUserId, slmId, callToAction, technicalName),
      ),
      catchError((error) => {
        this.currentUser = undefined;
        this.globalMessageService.add(
          formatTestPageError(error),
          GlobalMessageType.MSG_TYPE_ERROR,
          60000,
        );
        return of(undefined);
      }),
    );
  }

  /**
   * Fetches and sets the authorized roles for the current user.
   */
  getAuthorizedRoles(): void {
    getAuthorized(this.authStorageService, this.configService, this);
  }

  hasPreviewUrl(): boolean {
    return this.previewUrl?.length > 0;
  }

  togglePreview(targetUrl: string): void {
    this.previewUrl = this.previewUrl ? null : targetUrl;
    this.cdr.detectChanges();
  }
}
